import '../App.css';
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import authgear from "../authgear";
import Header from "../components/Header";

const base_url = process.env.NODE_ENV === "production"
    ? "https://hooks-28xd.onrender.com"
    : "http://localhost:4000";

const USERSCRIPT_URL = "https://hooks-28xd.onrender.com/showdown/hooks-showdown.user.js";
const ACCENT = "#00d4ff";
const WIN = "#4caf50";
const LOSS = "#ef5350";

const card = {
    background: "#1a1a2e",
    border: "1px solid #2a2a4a",
    borderRadius: "10px",
    padding: "18px 20px",
};

const inputStyle = {
    background: "#0d0d1a",
    border: "1px solid #2a2a4a",
    borderRadius: "6px",
    color: "#ddd",
    padding: "8px 10px",
    fontSize: "13px",
    boxSizing: "border-box",
};

const buttonStyle = {
    background: ACCENT,
    color: "#0a0a14",
    border: "none",
    borderRadius: "6px",
    padding: "8px 14px",
    fontSize: "13px",
    fontWeight: "bold",
    cursor: "pointer",
};

const sectionLabel = {
    fontSize: "11px",
    color: "#555",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: "12px",
};

const th = { textAlign: "left", padding: "6px 8px", color: "#666", fontWeight: "normal", fontSize: "12px" };
const td = { padding: "6px 8px", fontSize: "13px", borderTop: "1px solid #2a2a4a", verticalAlign: "top" };

function pct(n) {
    return `${Math.round((n || 0) * 100)}%`;
}

function formatDateTime(dateStr) {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

// "[Gen 9 Champions] VGC 2026 Reg M-C (Bo3)" -> "VGC 2026 Reg M-C (Bo3)"
function shortFormat(format) {
    return (format || "").replace(/^\[[^\]]*\]\s*/, "");
}

const Stat = ({ label, value, sub }) => (
    <div style={{ ...card, flex: "1 1 140px" }}>
        <div style={{ fontSize: "12px", color: "#888" }}>{label}</div>
        <div style={{ fontSize: "24px", color: "#eee", fontWeight: "bold", marginTop: "4px" }}>{value}</div>
        {sub && <div style={{ fontSize: "12px", color: "#666", marginTop: "2px" }}>{sub}</div>}
    </div>
);

const TallyTable = ({ title, rows }) => (
    <div style={{ ...card, flex: "1 1 300px" }}>
        <div style={sectionLabel}>{title}</div>
        {rows.length === 0 ? <div style={{ color: "#666", fontSize: "13px" }}>No data yet</div> : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr><th style={th}></th><th style={th}>Games</th><th style={th}>Win %</th></tr></thead>
                <tbody>
                    {rows.slice(0, 10).map(r => (
                        <tr key={r.key}>
                            <td style={{ ...td, color: "#ddd" }}>{r.key}</td>
                            <td style={{ ...td, color: "#aaa" }}>{r.games}</td>
                            <td style={{ ...td, color: r.winRate >= 0.5 ? WIN : LOSS }}>{pct(r.winRate)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        )}
    </div>
);

const TeamSheet = ({ title, side }) => (
    <div style={{ flex: "1 1 280px" }}>
        <div style={{ ...sectionLabel, marginBottom: "6px" }}>{title}: {side?.name}</div>
        {(side?.teamSheet?.length ? side.teamSheet : (side?.preview || []).map(species => ({ species }))).map(set => {
            const brought = side.brought?.includes(set.species);
            return (
                <div key={set.species} style={{ fontSize: "12px", color: brought ? "#ddd" : "#666", marginBottom: "6px" }}>
                    <span style={{ fontWeight: "bold" }}>{set.species}</span>
                    {set.item && <span> @ {set.item}</span>}
                    {set.ability && <span style={{ color: "#888" }}> · {set.ability}</span>}
                    {set.moves?.length > 0 && <div style={{ color: brought ? "#999" : "#555" }}>{set.moves.join(", ")}</div>}
                </div>
            );
        })}
    </div>
);

const Showdown = () => {
    const navigate = useNavigate();
    const [battles, setBattles] = useState([]);
    const [stats, setStats] = useState(null);
    const [format, setFormat] = useState("");
    const [formats, setFormats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [replayUrl, setReplayUrl] = useState("");
    const [importing, setImporting] = useState(false);
    const [importMessage, setImportMessage] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    const [notesDraft, setNotesDraft] = useState("");
    const [showSetup, setShowSetup] = useState(false);

    const getToken = async () => {
        await authgear.refreshAccessTokenIfNeeded();
        return authgear.accessToken;
    };

    const loadAll = useCallback(async () => {
        setError(null);
        try {
            const token = await getToken();
            const headers = { Authorization: `Bearer ${token}` };
            const params = format ? { format } : {};
            const [battlesRes, statsRes] = await Promise.all([
                axios.get(`${base_url}/showdown/battles`, { headers, params: { ...params, limit: 200 } }),
                axios.get(`${base_url}/showdown/stats`, { headers, params }),
            ]);
            setBattles(battlesRes.data.battles);
            setStats(statsRes.data);
            if (!format) {
                setFormats([...new Set(battlesRes.data.battles.map(b => b.formatId).filter(Boolean))]);
                if (battlesRes.data.battles.length === 0) setShowSetup(true);
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    }, [format]);

    useEffect(() => {
        if (authgear.sessionState !== "AUTHENTICATED") {
            navigate("/login");
            return;
        }
        loadAll();
    }, [navigate, loadAll]);

    const handleImport = async (e) => {
        e.preventDefault();
        if (!replayUrl.trim()) return;
        setImporting(true);
        setImportMessage(null);
        try {
            const token = await getToken();
            const { data } = await axios.post(`${base_url}/showdown/import-replay`, { url: replayUrl.trim() }, { headers: { Authorization: `Bearer ${token}` } });
            setImportMessage({ ok: true, text: `Imported: ${data.battle.result} vs ${data.battle.opponent?.name}` });
            setReplayUrl("");
            await loadAll();
        } catch (err) {
            setImportMessage({ ok: false, text: err.response?.data?.message || err.message });
        } finally {
            setImporting(false);
        }
    };

    const toggleExpanded = (battle) => {
        if (expandedId === battle._id) {
            setExpandedId(null);
            return;
        }
        setExpandedId(battle._id);
        setNotesDraft(battle.notes || "");
    };

    const saveNotes = async (battle) => {
        try {
            const token = await getToken();
            await axios.patch(`${base_url}/showdown/battles/${battle._id}`, { notes: notesDraft }, { headers: { Authorization: `Bearer ${token}` } });
            setBattles(bs => bs.map(b => b._id === battle._id ? { ...b, notes: notesDraft } : b));
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        }
    };

    const deleteBattle = async (battle) => {
        try {
            const token = await getToken();
            await axios.delete(`${base_url}/showdown/battles/${battle._id}`, { headers: { Authorization: `Bearer ${token}` } });
            setExpandedId(null);
            await loadAll();
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        }
    };

    const overall = stats?.overall;
    const series = stats?.series;

    return (
        <div style={{ minHeight: "100vh", background: "#0d0d1a", color: "#ddd", fontFamily: "Verdana, sans-serif" }}>
            <Header />
            <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "24px 16px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", marginBottom: "20px" }}>
                    <h1 style={{ margin: 0, fontSize: "24px", color: "#eee" }}>Pokémon Showdown</h1>
                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                        <select style={inputStyle} value={format} onChange={e => setFormat(e.target.value)}>
                            <option value="">All formats</option>
                            {formats.map(f => <option key={f} value={f}>{f}</option>)}
                        </select>
                        <button style={{ ...buttonStyle, background: "#2a2a4a", color: "#ddd" }} onClick={() => setShowSetup(s => !s)}>
                            Recorder setup
                        </button>
                    </div>
                </div>

                {error && <div style={{ ...card, borderColor: LOSS, color: LOSS, marginBottom: "16px" }}>{error}</div>}

                {showSetup && (
                    <div style={{ ...card, marginBottom: "16px", fontSize: "13px", lineHeight: 1.6 }}>
                        <div style={sectionLabel}>Recorder setup (once per machine)</div>
                        <ol style={{ margin: 0, paddingLeft: "20px", color: "#bbb" }}>
                            <li>Install the <a href="https://www.tampermonkey.net/" target="_blank" rel="noreferrer" style={{ color: ACCENT }}>Tampermonkey</a> extension. In Chrome/Edge, also turn on "Allow User Scripts" (or Developer mode) for it.</li>
                            <li>Open <a href={USERSCRIPT_URL} target="_blank" rel="noreferrer" style={{ color: ACCENT }}>{USERSCRIPT_URL}</a> and click Install. It auto-updates from that URL.</li>
                            <li>On play.pokemonshowdown.com, open the Tampermonkey menu → "Set Hooks API key" and paste the backend's MCP_API_KEY.</li>
                        </ol>
                        <div style={{ color: "#777", marginTop: "8px" }}>
                            Every battle you play is sent when it ends, saved replay or not. Uploads that fail (e.g. backend asleep) are queued in the browser and retried.
                        </div>
                    </div>
                )}

                {loading ? <div style={{ color: "#666" }}>Loading…</div> : (
                    <>
                        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "16px" }}>
                            <Stat label="Games" value={overall?.games ?? 0} sub={`${overall?.wins ?? 0}W – ${overall?.losses ?? 0}L`} />
                            <Stat label="Game win rate" value={pct(overall?.winRate)} />
                            <Stat label="Bo3 series" value={`${series?.won ?? 0}–${series?.lost ?? 0}`} sub={series?.incomplete ? `${series.incomplete} incomplete` : null} />
                            <Stat label="Rating" value={stats?.currentRating ?? "—"} sub="after last rated series" />
                        </div>

                        <form onSubmit={handleImport} style={{ ...card, display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center", marginBottom: "16px" }}>
                            <input
                                style={{ ...inputStyle, flex: "1 1 300px" }}
                                placeholder="Import a replay: https://replay.pokemonshowdown.com/…"
                                value={replayUrl}
                                onChange={e => setReplayUrl(e.target.value)}
                            />
                            <button style={buttonStyle} type="submit" disabled={importing}>{importing ? "Importing…" : "Import"}</button>
                            {importMessage && <div style={{ width: "100%", fontSize: "12px", color: importMessage.ok ? WIN : LOSS }}>{importMessage.text}</div>}
                        </form>

                        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "16px" }}>
                            <TallyTable title="My leads" rows={stats?.byMyLeads || []} />
                            <TallyTable title="My Pokémon brought" rows={stats?.byMyPokemonBrought || []} />
                            <TallyTable title="Vs. opponent Pokémon (preview)" rows={stats?.vsOpponentPokemonInPreview || []} />
                        </div>

                        <div style={card}>
                            <div style={sectionLabel}>Battles</div>
                            {battles.length === 0 ? <div style={{ color: "#666", fontSize: "13px" }}>No battles recorded yet.</div> : (
                                <div style={{ overflowX: "auto" }}>
                                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                        <thead>
                                            <tr>
                                                <th style={th}>When</th>
                                                <th style={th}>Format</th>
                                                <th style={th}>Opponent</th>
                                                <th style={th}>Result</th>
                                                <th style={th}>My leads</th>
                                                <th style={th}>Their leads</th>
                                                <th style={th}>Turns</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {battles.map(b => (
                                                <React.Fragment key={b._id}>
                                                    <tr onClick={() => toggleExpanded(b)} style={{ cursor: "pointer" }}>
                                                        <td style={{ ...td, color: "#888", whiteSpace: "nowrap" }}>{formatDateTime(b.startedAt)}</td>
                                                        <td style={{ ...td, color: "#aaa" }}>{shortFormat(b.format)}{b.gameNumber ? ` · G${b.gameNumber}` : ""}</td>
                                                        <td style={{ ...td, color: "#ddd" }}>{b.opponent?.name}</td>
                                                        <td style={{ ...td, color: b.result === "win" ? WIN : b.result === "loss" ? LOSS : "#aaa", fontWeight: "bold" }}>
                                                            {b.result.toUpperCase()}{b.endReason !== "normal" ? ` (${b.endReason})` : ""}
                                                        </td>
                                                        <td style={{ ...td, color: "#bbb" }}>{b.me?.leads?.join(" + ")}</td>
                                                        <td style={{ ...td, color: "#bbb" }}>{b.opponent?.leads?.join(" + ")}</td>
                                                        <td style={{ ...td, color: "#888" }}>{b.turns}</td>
                                                    </tr>
                                                    {expandedId === b._id && (
                                                        <tr>
                                                            <td colSpan={7} style={{ ...td, background: "#141428", padding: "14px" }}>
                                                                <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", marginBottom: "12px" }}>
                                                                    <TeamSheet title="Me" side={b.me} />
                                                                    <TeamSheet title="Opponent" side={b.opponent} />
                                                                </div>
                                                                <div style={{ fontSize: "12px", color: "#777", marginBottom: "10px" }}>
                                                                    Dimmed = not brought.
                                                                    {b.me?.megas?.length > 0 && ` My mega: ${b.me.megas.map(m => m.species).join(", ")}.`}
                                                                    {b.opponent?.megas?.length > 0 && ` Their mega: ${b.opponent.megas.map(m => m.species).join(", ")}.`}
                                                                    {b.me?.ratingAfter != null && ` Rating ${b.me.ratingBefore} → ${b.me.ratingAfter}.`}
                                                                    {b.replayUrl && <> <a href={b.replayUrl} target="_blank" rel="noreferrer" style={{ color: ACCENT }}>Replay</a></>}
                                                                </div>
                                                                <textarea
                                                                    style={{ ...inputStyle, width: "100%", minHeight: "60px", fontFamily: "inherit" }}
                                                                    placeholder="Notes (what went wrong, matchup plan…)"
                                                                    value={notesDraft}
                                                                    onChange={e => setNotesDraft(e.target.value)}
                                                                />
                                                                <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                                                                    <button style={buttonStyle} onClick={() => saveNotes(b)}>Save notes</button>
                                                                    <button style={{ ...buttonStyle, background: "transparent", color: LOSS, border: `1px solid ${LOSS}` }} onClick={() => deleteBattle(b)}>Delete</button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Showdown;
